// src/api/securityApi.ts
/**
 * The one axios instance for `/api/v1/mfa/**` (enrolment, verification, recovery) and
 * `/api/admin/security/**` (active sessions, revocation, peer MFA reset).
 *
 * WHY IT IS NOT THE SHARED `api` INSTANCE — the same reasoning that produced `campaignApi` and
 * `invoicingApi`, sharpened by what these endpoints are for. The shared instance signs the user out
 * on any 401 and on any 403 outside `/v1/auth`, and every failure this screen can produce is one of
 * those: a mistyped TOTP code, an unusable recovery code, a throttled retry, a step-up refusal.
 * Being signed out for mistyping a digit is bad anywhere; on the one screen an admin uses to get
 * their second factor working again it is a trap with no way out — the code they need is on the
 * device they are trying to enrol.
 *
 * So this instance ends the session for exactly one reason: a body that names a dead token. That
 * rule, and the transitional carve-out for a jar whose `badCode()` still answers 401, live in
 * `sessionErrors.ts` where a test can reach them. `campaignApi` and `invoicingApi` are untouched.
 */
import axios from 'axios';
import config from '../config';
import { store } from '../store/store';
import { logout } from '../reducers/authSlice';
import { stepUpRequired } from '../store/mfaSlice';
import { isMfaRequired } from './errors';
import { shouldSecurityApiLogout } from './sessionErrors';
import {
    ActiveSession,
    MfaStatus,
    RecoveryCodesBody,
    RevokedCount,
    TotpEnrollment,
    normalizeRecoveryCode,
    parseMfaStatus,
} from '../security/mfaContract';

/** `config.API_URL` already ends in `/api`, so these are the paths as the controllers map them. */
const MFA = '/v1/mfa';
const ADMIN_SECURITY = '/admin/security';

const securityApi = axios.create({
    baseURL: config.API_URL,
});

securityApi.interceptors.request.use(
    (requestConfig) => {
        const token = store.getState().auth.accessToken;
        if (token) {
            requestConfig.headers.Authorization = `Bearer ${token}`;
        }
        return requestConfig;
    },
    (error) => Promise.reject(error)
);

securityApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Order matters: the logout rule is asked first and is narrow, everything it declines falls
        // through to the caller with the session intact. Nothing is logged here — an axios error
        // carries the request headers, and those carry the bearer token.
        if (await shouldSecurityApiLogout(error)) {
            store.dispatch(logout());
        } else if (isMfaRequired(error)) {
            // Enforcement flipped on (or this token's verification was cleared server-side) while the
            // page was open. Telling the store lets the one modal host ask for a code, instead of
            // every caller inventing its own recovery from a 403.
            store.dispatch(stepUpRequired());
        }
        return Promise.reject(error);
    }
);

/* ---------------- enrolment and verification ---------------- */

/** Never persisted anywhere — see `mfaSlice`: `sessionVerified` belongs to one token row. */
export const fetchMfaStatus = async (): Promise<MfaStatus> => {
    const { data } = await securityApi.get(`${MFA}/status`);
    return parseMfaStatus(data);
};

/**
 * Starts (or restarts) enrolment. Calling this replaces the stored secret immediately and stops the
 * previous authenticator entry from producing valid codes — there is one secret column, not a
 * pending one and a live one. Callers must therefore only ever reach this from a deliberate click.
 */
export const enrollTotp = async (): Promise<TotpEnrollment> => {
    const { data } = await securityApi.post<TotpEnrollment>(`${MFA}/enroll`);
    return data;
};

/** Confirms enrolment and returns the ten plaintext recovery codes — the only time they exist
 *  outside the admin's own password manager. An empty array rather than a throw if the body is not
 *  the shape we expect: enrolment itself succeeded, and the screen has to say so. */
export const verifyEnrollment = async (code: string): Promise<string[]> => {
    const { data } = await securityApi.post<RecoveryCodesBody>(`${MFA}/verify-enrollment`, { code });
    return Array.isArray(data?.recoveryCodes) ? data.recoveryCodes : [];
};

/** Login-time step-up for an account that enrolled on a previous visit. */
export const verifyTotp = async (code: string): Promise<void> => {
    await securityApi.post(`${MFA}/verify`, { code });
};

/** Burns one recovery code. Normalised here so a code retyped off a printout with the dash left out,
 *  in lower case, or with a stray space, still matches — the server's own normaliser does not strip
 *  interior whitespace, and this endpoint allows five attempts per fifteen minutes. */
export const recoverWithCode = async (raw: string): Promise<void> => {
    await securityApi.post(`${MFA}/recover`, { recoveryCode: normalizeRecoveryCode(raw) });
};

/** Replaces every unused code with a fresh set. Requires step-up already — this is not a way in. */
export const regenerateRecoveryCodes = async (): Promise<string[]> => {
    const { data } = await securityApi.post<RecoveryCodesBody>(`${MFA}/recovery-codes/regenerate`);
    return Array.isArray(data?.recoveryCodes) ? data.recoveryCodes : [];
};

/* ---------------- sessions and emergencies ---------------- */

/** Every live token row. Guarded with `Array.isArray` so a proxy's HTML error page cannot reach the
 *  table renderer as if it were a list. */
export const listSessions = async (): Promise<ActiveSession[]> => {
    const { data } = await securityApi.get(`${ADMIN_SECURITY}/sessions`);
    return Array.isArray(data) ? (data as ActiveSession[]) : [];
};

/** Ends one token row. 404 means it was already gone, which callers may treat as success. */
export const revokeSession = async (tokenId: number): Promise<void> => {
    await securityApi.post(`${ADMIN_SECURITY}/sessions/${tokenId}/revoke`);
};

/** Ends every live session for one account, and answers how many rows that was. */
export const revokeUserSessions = async (userId: number): Promise<number> => {
    const { data } = await securityApi.post<RevokedCount>(
        `${ADMIN_SECURITY}/users/${userId}/revoke-sessions`
    );
    return typeof data?.revoked === 'number' ? data.revoked : 0;
};

/** The kill switch: every session, every account, including the caller's own. */
export const revokeAllSessions = async (): Promise<number> => {
    const { data } = await securityApi.post<RevokedCount>(`${ADMIN_SECURITY}/revoke-all-sessions`);
    return typeof data?.revoked === 'number' ? data.revoked : 0;
};

/** Clears another admin's enrolment and revokes their tokens, so they fall back through first-time
 *  setup on the next login. The two-admin answer to a lost phone and lost recovery codes. */
export const resetAdminMfa = async (userId: number): Promise<void> => {
    await securityApi.post(`${ADMIN_SECURITY}/admins/${userId}/reset-mfa`);
};

export default securityApi;
